import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../store";
import { addToCart } from "../../../../store/order";
import { graphql, navigate, useStaticQuery } from "gatsby";
import OrderInTheNext from "../../../ecommerce/OrderInTheNext";
import { getPriceToShow, getPriceValueToShow } from "../../../../utils/currency";
import ecommerceApi from "../../../../services/ecommerceApi";
import EcommerceApi from "../../../../services/ecommerceApi";
import { getProductImage } from "../../../../utils/image";
import Button from "../../../shared/Button";
import {
    ProductCategoryType,
    ProductDataType,
    ProductTaxCodeType,
    ProductType
} from "../../../../types";
import { useSalesChannel } from "../../../../context/SalesChannelContext";
import { ProductJsonLd } from "gatsby-plugin-next-seo";

export interface ProductSelectorProps {
    defaultProduct: ProductType;
    slug: string;
}

export default function ProductSelector({ defaultProduct: selectedProduct, slug }: ProductSelectorProps) {
    const dispatch = useDispatch<AppDispatch>();

    const order = useSelector((state: RootState) => state.order.order);
    const currencyCode = useSelector(
        (state: RootState) => state.currencyCode.selectedCurrencyCode?.currencyCode
    );
    const [quantity, setQuantity] = React.useState(1);

    const [selectedProductData, setSelectedProductData] = React.useState<ProductDataType | null>(null);
    const [adding, setAdding] = React.useState(false);

    useEffect(() => {
        loadProductData();
    }, [selectedProduct]);

    const loadProductData = async () => {
        if (selectedProduct) {
            const productId = selectedProduct.product || selectedProduct.id;
            const [product, taxCodes] = await Promise.all([
                ecommerceApi.getProductData(productId),
                ecommerceApi.getProductTaxCodesData(productId)
            ]);
            setSelectedProductData(product || null);
        }
    };

    const handleAddToCartClick = async () => {
        setAdding(true);
        const orderId = order ? order.id : null;
        const params = {
            orderId: orderId,
            skuId: selectedProduct.id,
            quantity: 1,
            salesChannel,
            currencyCode
        };
        await dispatch(addToCart(params));
        setAdding(false);
    };

    const salesChannel = useSalesChannel();

    let priceToShow = useMemo(() => {
        return getPriceToShow(
            selectedProductData?.currentPrice || selectedProduct.currentPrice,
            salesChannel,
            currencyCode,
            undefined,
            undefined,
            true,
            0,
            {
                small: "text-4xl",
                large: "text-6xl"
            }
        );
    }, [currencyCode, selectedProductData?.currentPrice, selectedProduct?.currentPrice]);

    const priceValue = useMemo(() => {
        return getPriceValueToShow(
            selectedProductData?.currentPrice || selectedProduct.currentPrice,
            salesChannel
        );
    }, [currencyCode, selectedProductData?.currentPrice, selectedProduct?.currentPrice]);

    const selectedSchema = useMemo(() => {
        if (selectedProduct?.schema) {
            try {
                const parsed = JSON.parse(selectedProduct.schema);

                // if not array
                if (!Array.isArray(parsed)) {
                    return parsed;
                }

                // if array
                const found = parsed.find(item => item.salesChannelId === salesChannel.id);
                return found;

            } catch (e) {
                console.warn(e);
            }
        }
        return null;
    }, [selectedProduct]);

    const reviewsSchema: any[] = useMemo(() => {
        return (selectedProduct?.reviews || []).map(rev => {

            const firstName = rev.author.split(" ")[0];
            const lastName = rev.author.split(" ")[1];

            return {
                "@type": "Review",
                author: {
                    "@type": "Person",
                    name: rev.author,
                    firstName,
                    lastName
                },
                datePublished: rev.datePublished,
                reviewBody: rev.body,
                name: rev.title,
                reviewRating: {
                    bestRating: "5",
                    ratingValue: rev.ratingValue,
                    worstRating: "0"
                }
            };
        });
    }, [selectedProduct.reviews]);

    return (
        <>
            <div className="flex justify-center">
                <div className="py-6 text-7xl" dangerouslySetInnerHTML={{ __html: priceToShow }}></div>
            </div>
            <div className="flex justify-center">
                <Button
                    textSize="text-xl"
                    className="w-1/2 mx-2"
                    padding="py-2 px-2"
                    onClick={handleAddToCartClick}
                    disabled={adding}
                    loader={adding}
                >
                    Sign Up
                </Button>
            </div>

            <ProductJsonLd
                name={selectedProduct?.SKUName}
                brand={selectedSchema?.brand}
                images={selectedProduct.imageFile?.publicURL ? [selectedProduct.imageFile?.publicURL] : undefined}
                gtin={selectedSchema?.gtin}
                offers={priceValue ? {
                    price: priceValue.toFixed(2),
                    priceCurrency: currencyCode || "GBP",
                    availability: selectedProduct.isTracked === false ? "InStock" : undefined,
                } : undefined}
                description={selectedSchema?.description || selectedProduct.shortDescription}
                mpn={selectedSchema?.mpn}
                aggregateRating={salesChannel.aggregateReview?.ratingValue ? {
                    ratingValue: salesChannel.aggregateReview?.ratingValue,
                    reviewCount: salesChannel.aggregateReview?.count
                } : undefined}
                overrides={{
                    "@type": "Product",
                    reviews: reviewsSchema
                }}
            />

        </>
    );
}
